import { useTranslation } from 'react-i18next'
import { Link2, Plus } from 'lucide-react'
import Link from '@/routing/Link'
import { Button } from '@/components/ui/button'
import { ROUTES } from '@/routes/routes'
import { PageHeader } from '@/components/layout/PageHeader'
import { TabsList, TabsTrigger } from '@/components/ui/tabs'
import type { KnowledgeLibraryHeaderProps } from '@/types/components/knowledge-library-header'

export function KnowledgeLibraryHeader({
  socialEnabled = false,
  view = 'docs',
  canLoadLibrary = false,
  canManageTrackers = false,
  onAddLinks,
  onAddTracker,
}: KnowledgeLibraryHeaderProps) {
  const { t } = useTranslation('app')
  const tabClassName =
    'h-12 rounded-none border-b-2 border-transparent bg-transparent px-0 py-3 font-normal shadow-none hover:text-foreground data-[state=active]:border-foreground data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none'

  const action = !canLoadLibrary ? null : view === 'social' ? (
    canManageTrackers ? (
      <Button
        variant="cta"
        shape="pill"
        className="h-9 px-4"
        onClick={onAddTracker}
        data-testid="social-add-tracker"
      >
        <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
        {t('socialListening.header.addTracker')}
      </Button>
    ) : null
  ) : (
    <div className="flex flex-wrap items-center gap-2">
      <Button
        variant="outline"
        shape="pill"
        className="h-9 px-4"
        onClick={onAddLinks}
        data-testid="knowledge-add-links"
      >
        <Link2 className="mr-2 h-4 w-4" aria-hidden="true" />
        {t('library.addLinks')}
      </Button>
      <Button
        asChild
        variant="cta"
        shape="pill"
        className="h-9 px-4"
        data-testid="knowledge-upload-cta"
      >
        <Link href={ROUTES.knowledgeUpload}>
          <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
          {t('library.upload')}
        </Link>
      </Button>
    </div>
  )

  return (
    <div className="flex min-w-0 flex-col gap-4" data-testid="knowledge-header">
      <PageHeader
        variant="lobby"
        title={t('library.title')}
        description={t('library.subtitle')}
        action={socialEnabled ? undefined : action}
      />
      {socialEnabled ? (
        <div className="flex flex-col-reverse gap-x-6 gap-y-2 border-b border-border sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <TabsList
              aria-label={t('library.title')}
              className="h-auto justify-start gap-6 rounded-none bg-transparent p-0"
              data-testid="knowledge-view-tabs"
            >
              <TabsTrigger
                value="docs"
                className={tabClassName}
                data-testid="knowledge-view-tab-docs"
              >
                {t('socialListening.tabs.myDocs')}
              </TabsTrigger>
              <TabsTrigger
                value="social"
                className={tabClassName}
                data-testid="knowledge-view-tab-social"
              >
                {t('socialListening.tabs.social')}
              </TabsTrigger>
            </TabsList>
          </div>
          {action ? (
            <div className="flex shrink-0 items-center gap-2 self-start sm:self-auto">
              {action}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}
